import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast } from 'react-toastify';
import { LuBug, LuImagePlus, LuLoaderCircle, LuSend, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { Field, TextArea } from '../../../components/form/FormControls';
import { auth, db, storage } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CARD, HelpNote, SectionHeader } from '../components/ProfileUi';

/**
 * "Sistem Hata Bildirimi": a bug report with an optional screenshot, saved to
 * ErrorFeedbacks for the developers.
 */
const ErrorReportForm = ({ userData }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.errorReport.${key}`, vars);
  const [errorSummary, setErrorSummary] = useState('');
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const handleErrorFeedbackSubmit = async () => {
    const user = auth.currentUser;
    if (!user || !errorSummary) {
      setError(label('required'));
      toast.error(label('required'));
      return;
    }
    setError('');
    setSending(true);
    try {
      let screenshotUrl = '';
      const timestamp = new Date().toISOString();
      if (screenshotFile) {
        const screenshotRef = ref(storage, `errorScreenshots/${user.uid}-${timestamp}.jpg`);
        await uploadBytes(screenshotRef, screenshotFile);
        screenshotUrl = await getDownloadURL(screenshotRef);
      }

      let userName = 'Bilinmeyen Kullanıcı';
      if (userData) {
        const firstName = userData.firstName || userData.name || userData.eName || '';
        const lastName = userData.lastName || userData.surname || userData.eSurname || '';
        userName = `${firstName} ${lastName}`.trim() || user.displayName || user.email || 'Bilinmeyen Kullanıcı';
      }

      await addDoc(collection(db, 'ErrorFeedbacks'), {
        errorSummary,
        screenshotUrl,
        timestamp,
        uid: user.uid,
        userName,
      });

      setErrorSummary('');
      setScreenshotFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success(label('sent'));
    } catch (submitError) {
      console.error('Error submitting feedback: ', submitError);
      toast.error(label('sendError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuBug}
        gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30"
        title={label('title')}
        description={label('description')}
      />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleErrorFeedbackSubmit();
        }}
        className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}
      >
        <HelpNote>{label('tips')}</HelpNote>
        <Field label={label('summary')} htmlFor="error-summary" required error={error || undefined}>
          <TextArea
            id="error-summary"
            rows={5}
            value={errorSummary}
            onChange={(event) => {
              setErrorSummary(event.target.value);
              if (error) setError('');
            }}
            placeholder={label('summaryPlaceholder')}
            error={error || undefined}
          />
        </Field>

        <Field label={label('screenshot')} htmlFor="error-screenshot" hint={label('screenshotHint')}>
          <input
            ref={fileInputRef}
            id="error-screenshot"
            type="file"
            accept="image/*"
            onChange={(event) => setScreenshotFile(event.target.files[0] || null)}
            className="tw-hidden"
          />
          {screenshotFile ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
              <LuImagePlus aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-teal-600" />
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-slate-700 dark:tw-text-slate-200">{screenshotFile.name}</span>
              <button
                type="button"
                onClick={() => {
                  setScreenshotFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                aria-label={label('removeScreenshot')}
                className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
              >
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            </div>
          ) : (
            <Button onClick={() => fileInputRef.current?.click()}>
              <LuImagePlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('chooseScreenshot')}
            </Button>
          )}
        </Field>

        <div className="tw-flex tw-justify-end">
          <Button variant="primary" type="submit" disabled={sending} className="max-sm:tw-w-full">
            {sending ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {sending ? label('sending') : label('submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};

ErrorReportForm.propTypes = { userData: PropTypes.object };

export default ErrorReportForm;
